/* eslint-disable max-classes-per-file, @typescript-eslint/no-explicit-any */
import { isArray, kebabCase, merge, mergeWith } from 'lodash';
import type { EventObject, GuardMeta } from 'xstate';
import { actions } from 'xstate';

import { wizardName } from '@swf/components/StarsWizard/Machine';
import type {
  FlowConfig,
  FlowItem,
  FlowItemOrArray,
  MachineOptions,
  StateDefinition,
  StateNodeConfig,
  WizardContext
} from '@swf/core/types/Wizard';
import { Action, Event, ForkState } from '@swf/core/types/Wizard';
import { getDisplayName } from '@swf/core/utils';

const { raise } = actions;

const getName = (name: string) => kebabCase(name.replace('Stars', ''));
const pageName = (component: React.ComponentType) => {
  const unHoc = (name: string): string => {
    const match = name.match(/\((.+)\)/);

    return match ? unHoc(match[1]) : name;
  };

  return getName(unHoc(getDisplayName(component)));
};
const getId = (name: string, parent?: NodeBase): string => (parent ? `${parent.id}.${name}` : name);

interface NodeMap {
  [key: string]: NodeBase;
}

export const nextPointer = `*${Event.NEXT}`;
export const previousPointer = `*${Event.PREVIOUS}`;
export const wizardJumpGuard = 'wizardJumpCheck';

class NodeBase {
  parent?: NodeBase;
  first?: NodeBase;
  last?: NodeBase;
  next?: NodeBase;
  previous?: NodeBase;
  children: NodeMap = {};

  constructor(public name: string) {}

  get id() {
    const { parent, name } = this;

    return getId(name, parent);
  }

  get idRef(): string {
    return `#${this.id}`;
  }

  get path(): string {
    const { parent, name } = this;

    return `${parent ? parent.path : ''}/${name}`;
  }

  has(name: string) {
    return !!this.children[name];
  }

  append(node: NodeBase) {
    const { last } = this;

    if (last) {
      last.next = node;
      node.previous = last;
    } else {
      this.first = node;
    }

    this.last = node;

    node.parent = this;
    this.children[node.name] = node;

    return node;
  }

  collect(name: string, value = {}) {
    const { first } = this;
    let result = value;

    if (first) {
      let current: Optional<NodeBase> = first;
      while (current) {
        result = merge({}, result, current[name as keyof typeof current]);
        current = current.next;
      }
    }

    return result;
  }
}

class FlowNode extends NodeBase {
  static fromItem(item: FlowItem) {
    if (item instanceof Invoke || item instanceof Fork) {
      return item;
    }

    return 'displayName' in item ? new Page(pageName(item)) : new State(item);
  }

  static fromConfig(config: FlowItemOrArray, parent: FlowNode) {
    const flow = isArray(config) ? config : [config];

    return flow.reduce((result, item) => {
      const child = FlowNode.fromItem(item);
      result.append(child);

      return result;
    }, parent);
  }

  // TODO: refactor. This is what it was before but implicit. It should be initialized or if undefined is ok it should be checked before usage
  rootValue = undefined as unknown as Node;
  isolated = undefined as unknown as boolean;

  get isolatedParent(): any {
    const { isolated, parent } = this;

    if (parent) {
      return isolated ? (parent as unknown as FlowNode).isolatedParent : parent;
    }
  }

  get id(): any {
    const { parent, name, isolatedParent } = this;

    return getId(name, isolatedParent || parent);
  }

  get root() {
    return this.rootValue;
  }

  set root(value: Node) {
    this.rootValue = value;

    Object.values(this.children).forEach((node: any) => (node.root = value));
  }

  get nextValue() {
    return {
      actions: Action.ValuesAssign,
      target: this.idRef
    };
  }

  get previousValue() {
    return this.idRef;
  }

  get nextNode(): any {
    const { next, parent } = this;

    return next || (parent && (parent as FlowNode).nextNode);
  }

  get previousNode(): any {
    const { previous, parent } = this;

    return previous || (parent && (parent as FlowNode).previousNode);
  }

  get on() {
    const { root, nextNode, previousNode } = this;
    const result: StateNodeConfig = {};

    if (nextNode) {
      result.on = {
        ...result.on,
        [Event.NEXT]: (nextNode as FlowNode).nextValue
      };
    }

    const { previous: rootPrevious } = root;
    const node = (previousNode as FlowNode) || (rootPrevious && (rootPrevious as Node).flow.last);

    if (node) {
      const { previousValue } = node;

      if (previousValue) {
        // this is making depositDetails go to its Fork(depositNoDetails) instead of depositInfo
        // TODO: merge this into FlowNode.previousValue()
        if (
          this.parent instanceof FlowNode &&
          this.parent.first?.idRef === this.idRef &&
          this.previousNode?.parent?.parent instanceof Fork
        ) {
          result.on = {
            ...result.on,
            [Event.PREVIOUS]: {
              target:
                this.parent?.parent?.idRef &&
                this.getForkStateId(this.parent.parent.idRef, ForkState.OnPrevious),
              actions: raise(Event.PREVIOUS)
            }
          };
        } else {
          result.on = {
            ...result.on,
            [Event.PREVIOUS]: previousValue
          };
        }
      }
    }

    return result;
  }

  get state() {
    const { parent, first, id } = this;
    const state: StateNodeConfig = { id };

    if (first) {
      if (!parent) {
        state.initial = first.name;
      }

      state.states = this.collect('config');
    }

    return state;
  }

  get config() {
    const { name, state } = this;

    return {
      [name]: state
    };
  }

  append(node: FlowNode) {
    super.append(node);
    node.root = this.root;
    node.isolated = this.isolated;

    return node;
  }

  appendIsolated(node: FlowNode) {
    this.append(node);

    const { next, previous } = node;
    if (next) {
      node.next = undefined;
      next.previous = undefined;
    }
    if (previous) {
      node.previous = undefined;
      previous.next = undefined;
    }

    node.isolated = true;

    return node;
  }

  getForkStateId(id: string, forkState: ForkState) {
    return `${id}.${forkState}`;
  }
}

class Page extends FlowNode {
  get state() {
    const { on, name, root, id } = this;
    const state: StateNodeConfig = {
      id,
      ...on
    };

    if (!root.has(name)) {
      state.meta = {
        navigate: true
      };
    }

    return state;
  }
}

class State extends FlowNode {
  definition: StateNodeConfig;

  constructor({ name, ...definition }: StateDefinition) {
    super(getName(name));

    this.definition = definition;
  }

  get previousValue() {
    const {
      previousNode,
      idRef,
      definition: { on }
    } = this;

    if (on && on[Event.PREVIOUS as unknown as keyof typeof on]) {
      return idRef;
    }

    return (previousNode as FlowNode)?.previousValue;
  }

  get state() {
    const { definition, id } = this;

    return Object.keys(definition).reduce(
      (result, key) => {
        const value = definition[key as keyof typeof definition];

        if (value && ['on', 'after'].includes(key)) {
          result[key] = this.getTargets(value);
        } else {
          result[key] = value;
        }

        return result;
      },
      { id } as Record<string, any>
    );
  }

  private getTarget(value: string) {
    const { nextNode, previousNode } = this;

    if (nextNode && value === nextPointer) {
      return nextNode.idRef;
    }

    if (previousNode && value === previousPointer) {
      return previousNode.previousValue;
    }

    return value;
  }

  private getTargets(map: Record<string, string>) {
    return Object.keys(map).reduce(
      (result, key) => ({ [key]: this.getTarget(map[key]), ...result }),
      {}
    );
  }
}

export class Invoke extends FlowNode {
  onDone: FlowNode;
  onError?: FlowNode;

  constructor(public src: string, onDone: FlowItem, onError?: FlowItem) {
    super(getName(src));

    this.onDone = this.appendIsolated(FlowNode.fromItem(onDone));
    if (onError) {
      this.onError = this.appendIsolated(FlowNode.fromItem(onError));
    }
  }

  get state() {
    const { src, onDone, onError, id } = this;

    return {
      id,
      invoke: {
        src,
        onDone: {
          target: onDone.idRef,
          actions: [Action.DataAssign, Action.ErrorReset]
        },
        onError: {
          target: onError ? onError.idRef : onDone.idRef,
          actions: Action.ErrorAssign
        }
      },
      meta: {
        waiting: true
      }
    };
  }

  get previousValue(): any {
    const { previousNode } = this;

    return previousNode && (previousNode as FlowNode).previousValue;
  }
}

export class Fork extends FlowNode {
  trueFlow: FlowNode;
  falseFlow: FlowNode;
  distinctFlows: boolean;

  constructor(public cond: string, trueFlow: FlowItemOrArray, falseFlow: FlowItemOrArray) {
    super(getName(cond));

    this.distinctFlows = isArray(trueFlow) || isArray(falseFlow);
    this.trueFlow = FlowNode.fromConfig(trueFlow, this.appendIsolated(new FlowNode('true')));
    this.falseFlow = FlowNode.fromConfig(falseFlow, this.appendIsolated(new FlowNode('false')));
  }

  get state() {
    const { trueFlow, falseFlow, id } = this;

    return {
      id,
      initial: ForkState.Eventless,
      states: {
        [ForkState.Eventless]: {
          id: this.getForkStateId(id, ForkState.Eventless),
          always: this.getTargets(trueFlow.first, falseFlow.first)
        },
        [ForkState.OnPrevious]: {
          id: this.getForkStateId(id, ForkState.OnPrevious),
          on: {
            [Event.PREVIOUS]: this.getPreviousOnFork()
          }
        }
      }
    };
  }

  get config() {
    const { name, state, mergedConfig } = this;

    return {
      [name]: state,
      ...mergedConfig
    };
  }

  get previousValue(): any {
    const { trueFlow, falseFlow, distinctFlows, previousNode } = this;

    return distinctFlows
      ? this.getPreviousTargets(trueFlow.last as FlowNode, falseFlow.last as FlowNode)
      : previousNode && (previousNode as FlowNode).previousValue;
  }

  get invertedCond() {
    return `!${this.cond}`;
  }

  private get mergedConfig() {
    const { trueFlow, falseFlow, distinctFlows } = this;

    return distinctFlows
      ? mergeWith(
          trueFlow.collect('config'),
          falseFlow.collect('config'),
          (trueState, falseState, key) =>
            trueFlow.has(key) ? this.mergeStates(trueState, falseState) : undefined
        )
      : {};
  }

  private mergeStates(trueState: any, falseState: any) {
    const { on: trueOn, invoke: trueInvoke } = trueState;
    const { on: falseOn, invoke: falseInvoke } = falseState;
    const onDone = 'onDone';
    const onError = 'onError';

    if (trueInvoke && falseInvoke) {
      return {
        ...trueState,
        invoke: {
          ...trueInvoke,
          [onDone]: this.mergeTransition(trueInvoke, falseInvoke, onDone),
          [onError]: this.mergeTransition(trueInvoke, falseInvoke, onError)
        }
      };
    }

    if (trueOn && falseOn && this.previousNode?.parent?.parent instanceof Fork) {
      const trueFlowRaise =
        this.trueFlow.last?.name === this.trueFlow.first?.name
          ? {
              target: this.getForkStateId(this.idRef, ForkState.OnPrevious),
              actions: raise(Event.PREVIOUS)
            }
          : null;

      const falseFlowRaise =
        this.falseFlow.last?.name === this.falseFlow.first?.name
          ? {
              target: this.getForkStateId(this.idRef, ForkState.OnPrevious),
              actions: raise(Event.PREVIOUS)
            }
          : null;

      // this make previous of amount raise previous to noDetails fork instead of depositInfo when depositNoDetails is true
      if (trueFlowRaise || falseFlowRaise) {
        const { cond, invertedCond } = this;
        return {
          ...trueState,
          on: {
            ...trueOn,
            [Event.PREVIOUS]:
              trueOn[Event.PREVIOUS] === falseOn[Event.PREVIOUS]
                ? trueOn[Event.PREVIOUS]
                : [
                    {
                      cond,
                      target: trueOn[Event.PREVIOUS],
                      ...trueFlowRaise
                    },
                    {
                      cond: invertedCond,
                      target: falseOn[Event.PREVIOUS],
                      ...falseFlowRaise
                    }
                  ]
          }
        };
      }
    }

    // this is removing redundant layer of array(state merge for array) in PREVIOUS condition for child Fork
    // TODO: merge this change into ForkNode.targets()
    return (
      trueOn &&
      falseOn && {
        ...trueState,
        on: {
          ...trueOn,
          [Event.PREVIOUS]:
            JSON.stringify(trueOn[Event.PREVIOUS]) === JSON.stringify(falseOn[Event.PREVIOUS])
              ? trueOn[Event.PREVIOUS]
              : this.targets(trueOn[Event.PREVIOUS] as string, falseOn[Event.PREVIOUS] as string)
        }
      }
    );
  }

  private mergeTransition(trueInvoke: any, falseInvoke: any, name: string) {
    const trueTransition = trueInvoke[name];
    const falseTransition = falseInvoke[name];
    const { target: trueTarget } = trueTransition;
    const { target: falseTarget } = falseTransition;

    if (trueTarget === falseTarget) {
      return trueTransition;
    }

    const { cond, invertedCond } = this;

    return [
      {
        cond,
        ...trueTransition
      },
      {
        cond: invertedCond,
        ...falseTransition
      }
    ];
  }

  private targets(trueName: string, falseName: string) {
    const { cond, invertedCond } = this;

    return trueName !== falseName
      ? [
          {
            cond,
            target: trueName
          },
          {
            cond: invertedCond,
            target: falseName
          }
        ]
      : trueName;
  }

  private getTargets(trueTarget?: NodeBase, falseTarget?: NodeBase) {
    if (!trueTarget || !falseTarget) {
      return;
    }

    return this.targets(trueTarget.idRef, falseTarget.idRef);
  }

  private getPreviousTargets(trueTarget?: FlowNode, falseTarget?: FlowNode) {
    if (!trueTarget || !falseTarget) {
      return;
    }

    return this.targets(trueTarget.previousValue, falseTarget.previousValue);
  }

  private getPreviousOnFork() {
    const defaultPreviousNode = this.previousNode && (this.previousNode as FlowNode).previousValue;
    // this make previous of depositInfoCheck Fork point to depositMethod
    const parentFlow = this.previousNode?.parent;
    const parentFork = parentFlow?.parent;
    if (parentFlow instanceof FlowNode && parentFork instanceof Fork) {
      const parentForkPreviousValue =
        parentFork.previousNode && (parentFork.previousNode as FlowNode).previousValue;
      const firstNameInTureFlow = parentFork.trueFlow.first?.name;
      const firstNameInFalseFlow = parentFork.falseFlow.first?.name;

      const trueTarget =
        firstNameInTureFlow !== this.name ? defaultPreviousNode : parentForkPreviousValue;
      const falseTarget =
        firstNameInFalseFlow !== this.name ? defaultPreviousNode : parentForkPreviousValue;

      return parentFork.targets(trueTarget, falseTarget);
    }

    return defaultPreviousNode;
  }
}

export class Node extends NodeBase {
  flow: FlowNode;

  constructor(
    name: string,
    flow: FlowConfig,
    private machineOptions: MachineOptions = {},
    parent?: Node | null
  ) {
    super(getName(name));

    const flowNode = FlowNode.fromConfig(flow, new FlowNode(this.name));
    flowNode.root = this;

    this.flow = flowNode;

    if (parent) {
      parent.append(this);
    }
  }

  get options(): MachineOptions {
    const { machineOptions, parent } = this;
    if (!parent) {
      machineOptions.guards = {
        ...machineOptions.guards,
        wizardJumpCheck: (context: WizardContext, event, { cond }: any): boolean =>
          context.data?.goJump?.target === cond.targetIdRef
      };
    }

    const { guards } = machineOptions;

    return this.collect(
      'options',
      guards
        ? {
            ...machineOptions,
            guards: Object.keys(guards).reduce(
              (result, key) => ({
                ...result,
                [key]: guards[key],
                [`!${key}`](
                  ...args: [WizardContext, EventObject, GuardMeta<WizardContext, EventObject>]
                ) {
                  return !guards[key]?.(...args);
                }
              }),
              {}
            )
          }
        : machineOptions
    );
  }

  get config() {
    const {
      flow: { name, config }
    } = this;
    const { states, id } = config[name];

    config[name].states = merge(this.collect('config'), states);

    if (id === wizardName && config[name].states) {
      const wizardStates = config[name].states;
      const addGroupJumps = (groupStates: any) =>
        Object.keys(groupStates).reduce((acc: object[], key) => {
          const curIdRef = `#${groupStates[key].id}`;
          const curResult = {
            target: curIdRef,
            cond: {
              type: wizardJumpGuard,
              targetIdRef: curIdRef
            }
          };

          acc.push(curResult);
          return acc;
        }, []);

      config[name].on = {
        [Event.JUMP]: Object.keys(wizardStates as object).reduce((acc: object[], key) => {
          const keyStates = wizardStates?.[key]?.states;
          const curResult = keyStates ? addGroupJumps(keyStates) : [];

          acc.push(...curResult);
          return acc;
        }, [])
      };
    }

    return config;
  }

  getPath(component: any) {
    return `${this.path}/${pageName(component)}`;
  }
}
